import React, { useCallback, useEffect, useRef, useState } from "react";
import { useFocusEffect } from "@react-navigation/native";
import { ActivityIndicator, View } from "react-native";

import NearbyLocationPermissionScreen from "../NearbyLocationPermissionScreen";

import { AppUserSession } from "@/schemas/auth/session";
import { RecommendationCategoryFeed } from "@/schemas/user/explore";
import ExploreCategoryScreenBase from "@/screens/Explore/ExploreCategory/Components/CategoryScreen/ExploreByCategoryScreenBase";
import SmallProfileCard from "@/screens/Explore/Components/ProfileCard/SmallProfileCard";
import { hasLocationPermission, registerUserLocation } from "@/utils/location";

type Props = {
  session: AppUserSession;
  categoryData: RecommendationCategoryFeed;
  isDevTab?: boolean;
  isMarketingMode?: boolean;
};

function ExploreNearbyCategoryScreen({
  session,
  categoryData,
  isDevTab = false,
  isMarketingMode = false,
}: Props): React.JSX.Element {
  const [isLocationPermissionGranted, setIsLocationPermissionGranted] =
    useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [shouldRefetchOnMount, setShouldRefetchOnMount] = useState(false);
  const [refetchFn, setRefetchFn] = useState<(() => Promise<void>) | null>(
    null,
  );
  const prevPermissionRef = useRef<null | boolean>(null);

  const checkLocationPermission = useCallback(async (): Promise<boolean> => {
    setIsChecking(true);
    const wasGrantedBefore = prevPermissionRef.current;
    const isGranted = await hasLocationPermission();
    setIsLocationPermissionGranted(isGranted);
    setIsChecking(false);

    // Mark that we need to refetch after the base component mounts
    if (wasGrantedBefore === false && isGranted) {
      setShouldRefetchOnMount(true);
    }
    prevPermissionRef.current = isGranted;
    return isGranted;
  }, []);

  // Trigger refetch after ExploreCategoryScreenBase mounts and registers its refetch function
  useEffect(() => {
    if (shouldRefetchOnMount && refetchFn) {
      refetchFn();
      setShouldRefetchOnMount(false);
    }
  }, [shouldRefetchOnMount, refetchFn]);

  // Memoize to prevent infinite loop (new callback -> useEffect in child -> setRefetchFn -> re-render)
  const handleRegisterRefetch = useCallback((refetch: () => Promise<void>) => {
    setRefetchFn(() => refetch);
  }, []);

  // Check location permission and register user location on focus
  useFocusEffect(
    useCallback(() => {
      (async (): Promise<void> => {
        const isGranted = await checkLocationPermission();
        if (isGranted) {
          registerUserLocation(session); // backend will handle rate-limiting
        }
      })();
    }, [checkLocationPermission, session]),
  );

  if (isChecking) {
    return (
      <View style={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
        <ActivityIndicator size="large" />
      </View>
    );
  }

  if (isLocationPermissionGranted) {
    return (
      <ExploreCategoryScreenBase
        session={session}
        categoryData={categoryData}
        isDevTab={isDevTab}
        isMarketingMode={isMarketingMode}
        numOfColumns={3}
        registerRefetch={handleRegisterRefetch}
        renderProfileCard={({
          item,
          itemWidth,
          itemHeight,
          onLockedProfileClick,
        }) => (
          <SmallProfileCard
            profile={item}
            itemWidth={itemWidth}
            itemHeight={itemHeight}
            displayHighlights={false}
            cardState={item.isLocked ? "locked" : "normal"}
            onNonNormalStateCardClick={onLockedProfileClick}
          />
        )}
      />
    );
  } else {
    return (
      <NearbyLocationPermissionScreen
        profileCount={categoryData.items.total}
        onPostPermissionRequest={checkLocationPermission}
      />
    );
  }
}

export default ExploreNearbyCategoryScreen;
